import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal, Type } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent } from '@examdojo/core/icon';
import { MarkdownViewerComponent } from '@examdojo/core/ui/markdown-viewer';
import { SuperLatexComponent } from '@examdojo/super-latex';
import { FormulaCalloutBlock } from '../../../model';

@Component({
  selector: 'dojo-formula-callout-block-renderer',
  template: `
    <div
      class="formula-callout formula-callout-container overflow-hidden"
      [class.rounded-2xl]="!block().borderless"
      [class.border]="!block().borderless"
      [class.border-neutral-200]="!block().borderless"
    >
      <!-- Formula + Explanation row -->
      <div
        class="formula-row px-4"
        [class.py-4]="!block().borderless"
        [class.bg-neutral-50]="!block().borderless"
        [class.formula-row-no-center]="block().center_formula === false"
        [class.formula-row-no-labels]="!block().explanation"
      >
        @if (resolvedFormulaBookletUrl()) {
          <a
            class="formula-booklet-btn"
            [href]="resolvedFormulaBookletUrl()"
            target="_blank"
            rel="noopener noreferrer"
            matTooltip="This is given in the formula booklet"
          >
            <dojo-icon icon="paperclip" faPrefix="fas" [size]="16" />
          </a>
        } @else if (block().formula_booklet) {
          <span class="formula-booklet-btn" matTooltip="This is given in the formula booklet">
            <dojo-icon icon="paperclip" faPrefix="fas" [size]="16" />
          </span>
        }
        @if (block().center_formula !== false && block().explanation) {
          <div class="formula-spacer"></div>
        }
        <!-- Formula (SuperLatex) -->
        <div class="formula-area">
          <dojo-super-latex [content]="block().formula" fontSize="1.2em" />
        </div>

        <!-- Explanation -->
        @if (block().explanation) {
          <div class="explanation-area text-neutral-600">
            <dojo-markdown-viewer [content]="block().explanation" />
          </div>
        }
      </div>

      <!-- Derivation (collapsible) -->
      @if (block().show_derivation && block().content.length) {
        <div class="bg-neutral-0 border-t border-neutral-200 px-4 py-2">
          <button
            type="button"
            class="toggle-header flex items-center gap-1 text-sm font-medium text-neutral-400"
            (click)="toggleDerivation()"
            [attr.aria-expanded]="derivationExpanded()"
          >
            <span class="toggle-icon text-[0.5rem] transition-transform" [class.rotate-90]="derivationExpanded()"
              >&#9654;</span
            >
            Show derivation
          </button>

          @if (derivationExpanded()) {
            <div class="mt-2">
              <ng-container *ngComponentOutlet="contentRenderer(); inputs: { blocks: block().content }" />
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: `
    $breakpoint-stack: 500px;

    :host {
      @apply block my-6;
    }

    .formula-callout-container {
      container-type: inline-size;
      position: relative;
    }

    .formula-booklet-btn {
      position: absolute;
      top: 11px;
      left: 11px;
      color: var(--neutral-400);
      z-index: 5;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      text-decoration: none;
      transition: color 0.15s;

      &:hover {
        color: var(--neutral-500);
      }

      @container (max-width: #{$breakpoint-stack}) {
        position: relative;
        top: auto;
        left: auto;
        align-self: flex-start;
      }
    }

    .formula-row {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      column-gap: 1.5rem;
      align-items: center;

      @container (max-width: #{$breakpoint-stack}) {
        display: flex;
        flex-direction: column;
      }
    }

    .formula-row-no-labels {
      grid-template-columns: 1fr;
    }

    .formula-row-no-center {
      grid-template-columns: minmax(33.33%, 50%) 1fr;

      .formula-spacer {
        display: none;
      }

      @container (max-width: #{$breakpoint-stack}) {
        grid-template-columns: 1fr;
      }
    }

    .formula-spacer {
      @container (max-width: #{$breakpoint-stack}) {
        display: none;
      }
    }

    .formula-area {
      display: flex;
      justify-content: center;
      min-width: 0;
      overflow: hidden;

      @container (max-width: #{$breakpoint-stack}) {
        width: 100%;
      }
    }

    .explanation-area {
      @container (min-width: calc(#{$breakpoint-stack} + 1px)) {
        border-left: 1px solid var(--neutral-200);
        padding-left: 1.5rem;

        ::ng-deep {
          p {
            font-size: 0.84rem !important;
            line-height: 1.4 !important;
            margin: 0;
          }
          .katex {
            font-size: 1em !important;
          }
        }
      }

      @container (max-width: #{$breakpoint-stack}) {
        width: 100%;
        border-top: 1px solid var(--neutral-200);
        margin-top: 1rem;
        padding: 1rem 0 0 0;
      }
    }

    .toggle-header {
      cursor: pointer;
      background: transparent;
      border: none;
      padding: 0.25rem 0;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SuperLatexComponent, MarkdownViewerComponent, NgComponentOutlet, MatTooltipModule, IconComponent],
})
export class FormulaCalloutBlockRendererComponent {
  readonly block = input.required<FormulaCalloutBlock>();
  readonly contentRenderer = input.required<Type<unknown>>();
  readonly defaultFormulaBookletUrl = input<string>('');

  readonly resolvedFormulaBookletUrl = computed(() => {
    const b = this.block();
    if (b.formula_booklet_url) {
      return b.formula_booklet_url;
    }
    if (b.formula_booklet) {
      return this.defaultFormulaBookletUrl();
    }
    return '';
  });

  readonly derivationExpanded = signal(false);

  toggleDerivation(): void {
    this.derivationExpanded.update((v) => !v);
  }
}
